import { CheckCircle, Info, Warning, WarningCircle } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const alertVariants = cva("bfrs-flex bfrs-rounded-xl bfrs-border", {
  variants: {
    tone: {
      info: "bfrs-border-info/20 bfrs-bg-info-soft bfrs-text-info-contrast",
      success: "bfrs-border-success/20 bfrs-bg-success-soft bfrs-text-success-contrast",
      warning: "bfrs-border-warning/20 bfrs-bg-warning-soft bfrs-text-warning-contrast",
      danger: "bfrs-border-danger/20 bfrs-bg-danger-soft bfrs-text-danger"
    },
    size: {
      xs: "bfrs-gap-1 bfrs-p-1.5 bfrs-text-[10px]",
      sm: "bfrs-gap-2 bfrs-p-2.5 bfrs-text-xs",
      md: "bfrs-gap-3 bfrs-p-4 bfrs-text-sm",
      lg: "bfrs-gap-4 bfrs-p-5 bfrs-text-base"
    }
  },
  defaultVariants: {
    tone: "info",
    size: "md"
  }
});

const iconSizeMap = { xs: 10, sm: 14, md: 18, lg: 20 } as const;

const iconMap = {
  info: Info,
  success: CheckCircle,
  warning: Warning,
  danger: WarningCircle
};

export type AlertProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> &
  VariantProps<typeof alertVariants> & {
    title?: ReactNode;
    action?: ReactNode;
    icon?: ReactNode;
  };

export const Alert = forwardRef<HTMLDivElement, AlertProps>(({ className, tone, size = "md", title, action, icon, children, ...props }, ref) => {
  const resolvedTone = tone ?? "info";
  const Icon = iconMap[resolvedTone];
  return (
    <div ref={ref} role={resolvedTone === "danger" ? "alert" : "status"} className={cn(alertVariants({ tone: resolvedTone, size }), className)} {...props}>
      <span className="bfrs-mt-0.5 bfrs-shrink-0">{icon ?? <Icon size={iconSizeMap[size ?? "md"]} weight="bold" />}</span>
      <div className="bfrs-min-w-0 bfrs-flex-1">
        {title && <div className="bfrs-mb-1 bfrs-font-semibold bfrs-text-text-primary">{title}</div>}
        <div className="bfrs-leading-5">{children}</div>
      </div>
      {action && <div className="bfrs-shrink-0">{action}</div>}
    </div>
  );
});

Alert.displayName = "Alert";
